/** Partial or complete month + year for profile career-history fields. */
export type MonthYearDraft = {
  month?: number
  year?: number
}

export type CompleteMonthYear = {
  month: number
  year: number
}

export const MONTH_FULL_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export const MONTH_SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const NONE_VALUE = 'none'

export const MONTH_YEAR_NONE_VALUE = NONE_VALUE

export function isEmptyMonthYear(value?: MonthYearDraft | null): boolean {
  return !value?.month && !value?.year
}

export function isCompleteMonthYear(
  value?: MonthYearDraft | null,
): value is CompleteMonthYear {
  return Boolean(value?.month && value?.year)
}

export function isPartialMonthYear(value?: MonthYearDraft | null): boolean {
  return !isEmptyMonthYear(value) && !isCompleteMonthYear(value)
}

export function compareMonthYear(a: CompleteMonthYear, b: CompleteMonthYear): number {
  if (a.year !== b.year) return a.year - b.year
  return a.month - b.month
}

/** Persist as the first of the month so existing DateField APIs stay unchanged. */
export function monthYearToIso(value: CompleteMonthYear): string {
  return `${value.year}-${String(value.month).padStart(2, '0')}-01`
}

export function parseIsoToMonthYear(iso?: string | null): MonthYearDraft | undefined {
  if (!iso) return undefined
  const match = /^(\d{4})-(\d{2})/.exec(iso)
  if (!match) return undefined
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isInteger(year) || month < 1 || month > 12) return undefined
  return { year, month }
}

export function monthYearDraftKey(value?: MonthYearDraft | null): string {
  if (isEmptyMonthYear(value)) return ''
  return `${value?.year ?? ''}-${value?.month ?? ''}`
}

export function formatMonthYearShort(value: CompleteMonthYear): string {
  return `${MONTH_SHORT_NAMES[value.month - 1]} ${value.year}`
}

export const PROFILE_YEAR_PAST_SPAN = 80
export const PROFILE_YEAR_EXPIRY_FUTURE_SPAN = 20

/** Years newest-first. Default is this year and earlier (career history). */
export function profileYearOptions(
  now = new Date(),
  options?: { futureYears?: number; pastYears?: number },
): number[] {
  const current = now.getFullYear()
  const latest = current + (options?.futureYears ?? 0)
  const earliest = current - (options?.pastYears ?? PROFILE_YEAR_PAST_SPAN)
  const years: number[] = []
  for (let year = latest; year >= earliest; year -= 1) {
    years.push(year)
  }
  return years
}

/** Keep a saved year visible even if it sits outside the usual range. */
export function yearOptionsIncluding(years: number[], selected?: number): number[] {
  if (!selected || years.includes(selected)) return years
  return [...years, selected].sort((a, b) => b - a)
}

export function patchMonthYear(
  current: MonthYearDraft | undefined,
  patch: { month?: number | null; year?: number | null },
): MonthYearDraft | undefined {
  const next: MonthYearDraft = { ...current }
  if (patch.month === null) delete next.month
  else if (patch.month !== undefined) next.month = patch.month
  if (patch.year === null) delete next.year
  else if (patch.year !== undefined) next.year = patch.year
  return isEmptyMonthYear(next) ? undefined : next
}
